import { Injectable, inject } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import type { DocumentData, QueryFn } from '@newgenus/fire-compat/firestore';
import {
    OrgDraftPart,
    OrgDraftSnapshotPayload,
    OrgHatTemplate,
    OrgPolicyTemplate,
    OrgSecurityGroupTemplate,
    OrgTemplate,
    OrgTemplateScope,
} from '@newgenus/common';

/**
 * Lightweight summary of one of the current user's organizations — what the
 * "Clone an org I admin" picker needs to render a row.
 *
 * Loaded by walking `users/{uid}.organizations` (already populated for the
 * header dropdown), then `partCount` from `organizations/{orgKey}.orgParts`
 * length (denormalised on the org root doc, no fan-out reads).
 */
export interface CloneableOrgSummary {
    orgKey: string;
    name: string;
    partCount: number;
    /** Surfaces a friendly "Updated 3 days ago" / etc. in the picker. */
    modifiedMs: number;
}

/**
 * Provenance of a template surfaced in the wizard picker — drives the
 * little source chip shown next to each row.
 */
export type TemplateSourceTag = 'system' | 'shared' | 'org-private';

/**
 * One row in the picker (P4.d). The picker shows a unified list across all
 * three visibility tiers; the `sourceTag` is the chip the row renders.
 */
export interface PickerTemplate {
    template: OrgTemplate;
    sourceTag: TemplateSourceTag;
    /** Set when `sourceTag === 'org-private'` so the chip can read "from <orgName>". */
    ownerOrgName?: string;
}

/**
 * P4.a + P4.b service backing the wizard's Step 1 "Curated template" and
 * "Clone an org I admin" cards.
 *
 * - **System templates** are world-readable per `firestore.rules:85-93` so the
 *   list query is unauthenticated-safe (any signed-in user can read).
 * - **Shared templates** (sharedWithOrgKeys array-contains-any user's orgs)
 *   are scoped to P4.c+ — not surfaced here yet.
 * - **Clone source orgs** come from `users/{uid}.organizations` — the map
 *   already loaded for the header dropdown. We re-read it as a one-shot rather
 *   than wire into NgRx (the lib is shared between `newgenus-frontend` +
 *   `admin-tool`, which doesn't have the same store).
 *
 * Future:
 *  - P4.c callable wrapper lives in `OrgCreationService.publishOrgAsTemplate`.
 *  - P5 will extend `loadAvailableTemplates()` to surface `hats`/`SGs`/etc.
 *    once the v2 template snapshot shape lands.
 */
@Injectable({ providedIn: 'root' })
export class OrgTemplateService {
    private db = inject(AngularFirestore);
    private auth = inject(AngularFireAuth);

    /**
     * Curated system templates seeded by
     * `apps/newgenus-firebase-scripts/src/system-config/seed-org-templates.js`.
     * Returned in name-ascending order so the picker renders deterministically.
     */
    public listSystemTemplates(): Observable<OrgTemplate[]> {
        return this.db
            .collection<OrgTemplate>('orgTemplates', (ref) => ref.where('visibility', '==', 'system').orderBy('name'))
            .valueChanges({ idField: 'key' });
    }

    /**
     * Unified list of templates available to the current user (P4.d). One-shot
     * read; the picker doesn't need live updates.
     *
     * Three Firestore queries fan out in parallel:
     *  1. `visibility === 'system'`
     *  2. `visibility === 'shared' AND sharedWithOrgKeys array-contains-any [my orgs]`
     *  3. `visibility === 'org-private' AND ownerOrgKey IN [my orgs]`
     *
     * Firestore caps `array-contains-any` and `in` at 10 entries — for users
     * with >10 orgs we chunk the orgKeys and run multiple queries per branch.
     * Results are merged + deduped by templateKey (system always wins on
     * conflict; then shared; then org-private).
     */
    public async loadAvailableTemplates(): Promise<PickerTemplate[]> {
        const myOrgKeys = await this.loadMyOrgKeys();

        // Each branch is fail-soft: if a Firestore rules / index / network
        // hiccup rejects one query, the other tiers still surface in the
        // picker. The user sees what they can use; the failure is logged.
        const systemPromise = firstValueFrom(this.listSystemTemplates()).catch((err) => {
            console.warn('[OrgTemplateService] system templates query failed:', err);
            return [] as OrgTemplate[];
        });

        const sharedPromise =
            myOrgKeys.length === 0
                ? Promise.resolve([] as OrgTemplate[])
                : this.runChunkedQuery(
                      myOrgKeys,
                      (chunk) => (ref) =>
                          ref
                              .where('visibility', '==', 'shared')
                              .where('sharedWithOrgKeys', 'array-contains-any', chunk)
                  ).catch((err) => {
                      console.warn('[OrgTemplateService] shared templates query failed:', err);
                      return [] as OrgTemplate[];
                  });

        const privatePromise =
            myOrgKeys.length === 0
                ? Promise.resolve([] as OrgTemplate[])
                : this.runChunkedQuery(
                      myOrgKeys,
                      (chunk) => (ref) => ref.where('visibility', '==', 'org-private').where('ownerOrgKey', 'in', chunk)
                  ).catch((err) => {
                      console.warn('[OrgTemplateService] org-private templates query failed:', err);
                      return [] as OrgTemplate[];
                  });

        const [sys, shared, priv] = await Promise.all([systemPromise, sharedPromise, privatePromise]);

        const ownerOrgNamesCache = new Map<string, string>();
        const byKey = new Map<string, PickerTemplate>();

        for (const t of sys) {
            byKey.set(t.key, { template: t, sourceTag: 'system' });
        }
        for (const t of shared) {
            if (!byKey.has(t.key)) {
                byKey.set(t.key, { template: t, sourceTag: 'shared' });
            }
        }
        for (const t of priv) {
            if (byKey.has(t.key)) continue;
            const entry: PickerTemplate = { template: t, sourceTag: 'org-private' };
            if (t.ownerOrgKey) {
                entry.ownerOrgName = await this.resolveOrgName(t.ownerOrgKey, ownerOrgNamesCache);
            }
            byKey.set(t.key, entry);
        }

        return Array.from(byKey.values()).sort((a, b) => {
            // system → shared → org-private; within each, alpha by name.
            const order: Record<TemplateSourceTag, number> = { system: 0, shared: 1, 'org-private': 2 };
            const d = order[a.sourceTag] - order[b.sourceTag];
            return d !== 0 ? d : (a.template.name || '').localeCompare(b.template.name || '');
        });
    }

    /**
     * One-shot read of the user's organizations + part counts. Returns orgs
     * sorted by `modifiedMs` desc so the most-recently-touched candidate is at
     * the top of the picker.
     *
     * `partCount` is read off the org's `orgParts` denormalised array (the same
     * value the header / sidebar already trusts). Orgs the user has no read
     * access to are silently dropped — Firestore rules already gate the
     * `organizations/{orgKey}` read, so a missing doc just means "this user
     * lost access to that org since their user doc was last updated".
     */
    public async loadCloneableOrgs(): Promise<CloneableOrgSummary[]> {
        const user = await firstValueFrom(
            this.auth.user.pipe(
                filter((u) => !!u),
                take(1)
            )
        );
        if (!user) return [];

        const userDoc = await firstValueFrom(
            this.db.doc<{ organizations?: Record<string, unknown> }>(`users/${user.uid}`).get()
        );
        const orgsMap = (userDoc.data()?.organizations ?? {}) as Record<string, unknown>;
        const orgKeys = Object.keys(orgsMap).filter((k) => k && typeof k === 'string');
        if (orgKeys.length === 0) return [];

        const summaries = await Promise.all(orgKeys.map(async (orgKey) => this.summariseOrg(orgKey)));

        return summaries.filter((s): s is CloneableOrgSummary => !!s).sort((a, b) => b.modifiedMs - a.modifiedMs);
    }

    /**
     * Strip the live `OrgPart[]` of an existing org down to the wizard's
     * `OrgDraftPart[]` shape. Used when the user picks a Clone-source.
     *
     * Field whitelist matches `OrgDraftPart` exactly so we don't drag
     * `modifiedOn` / runtime-derived fields into the draft autosave path
     * (Firestore rejects unknown server-side timestamps from the client).
     */
    public async loadOrgStructureAsDraftParts(orgKey: string): Promise<OrgDraftPart[]> {
        const snap = await firstValueFrom(this.db.doc<{ orgParts?: unknown[] }>(`organizations/${orgKey}`).get());
        const rawParts = (snap.data()?.orgParts ?? []) as Array<Record<string, unknown>>;
        return rawParts.map((p) => this.toDraftPart(p));
    }

    /**
     * Template v2 — full snapshot (parts + hats + SGs + policies) of an
     * existing org, used by the Clone-an-org path so a clone carries the
     * same structural IP a published template would. Fail-soft per
     * subcollection: a Firestore rules denial on hats / SGs / policies
     * just omits that tier rather than failing the whole load.
     *
     * `scope` is computed from which tiers actually had data; absent tiers
     * are simply omitted from the returned payload (which makes the
     * orchestrator's apply stages no-op for them).
     */
    public async loadOrgFullSnapshot(orgKey: string): Promise<{
        parts: OrgDraftPart[];
        snapshotPayload?: OrgDraftSnapshotPayload;
    }> {
        const parts = await this.loadOrgStructureAsDraftParts(orgKey);
        const ceoPostKey = parts.find((p) => p.isCeoPost === true)?.partKey ?? null;

        const [hats, securityGroups, policies] = await Promise.all([
            this.loadHatsAsTemplate(orgKey, ceoPostKey).catch((err) => {
                console.warn(`[OrgTemplateService] hats subcollection read failed for clone ${orgKey}:`, err);
                return undefined;
            }),
            this.loadSGsAsTemplate(orgKey).catch((err) => {
                console.warn(`[OrgTemplateService] securityGroups subcollection read failed for clone ${orgKey}:`, err);
                return undefined;
            }),
            this.loadPoliciesAsTemplate(orgKey).catch((err) => {
                console.warn(`[OrgTemplateService] policies subcollection read failed for clone ${orgKey}:`, err);
                return undefined;
            }),
        ]);

        const scope: OrgTemplateScope[] = ['structure', 'features', 'settings'];
        const payload: OrgDraftSnapshotPayload = { snapshotVersion: 2, scope };
        if (hats && hats.length) {
            payload.hats = hats;
            scope.push('hats');
        }
        if (securityGroups && securityGroups.length) {
            payload.securityGroups = securityGroups;
            scope.push('securityGroups');
        }
        if (policies && policies.length) {
            payload.policies = policies;
            scope.push('policies');
        }

        // Only attach the payload when at least one v2 tier carried data —
        // otherwise the wizard treats the clone as v1 (structure only).
        const hasV2Data = !!(payload.hats || payload.securityGroups || payload.policies);
        return hasV2Data ? { parts, snapshotPayload: payload } : { parts };
    }

    private async loadHatsAsTemplate(orgKey: string, ceoPostKey: string | null): Promise<OrgHatTemplate[]> {
        const snap = await firstValueFrom(this.db.collection(`organizations/${orgKey}/hats`).get());
        const out: OrgHatTemplate[] = [];
        snap.forEach((d) => {
            const p = d.data() as Record<string, unknown>;
            if (p['isSoftDeleted'] === true) return;
            const key = String(p['key'] ?? d.id);
            if (!key) return;
            const positionArray = Array.isArray(p['positionArray'])
                ? (p['positionArray'] as unknown[]).filter((k): k is string => typeof k === 'string' && !!k)
                : [];
            const sgsRaw = Array.isArray(p['securityGroupsArray']) ? (p['securityGroupsArray'] as unknown[]) : [];
            const securityGroupsArray: string[] = [];
            for (const e of sgsRaw) {
                if (typeof e === 'string' && e) securityGroupsArray.push(e);
                else if (e && typeof e === 'object' && 'key' in e) {
                    const k = (e as { key?: unknown }).key;
                    if (typeof k === 'string' && k) securityGroupsArray.push(k);
                }
            }
            const hat: OrgHatTemplate = {
                key,
                name: String(p['name'] ?? ''),
                positionArray,
                securityGroupsArray,
            };
            if (typeof p['description'] === 'string') hat.description = p['description'];
            if (p['policyKeys'] && typeof p['policyKeys'] === 'object') {
                hat.policyKeys = p['policyKeys'] as Record<string, string>;
            }
            if (typeof p['isMultiUsers'] === 'boolean') hat.isMultiUsers = p['isMultiUsers'];
            if (ceoPostKey && positionArray.includes(ceoPostKey)) hat.isCeoHat = true;
            out.push(hat);
        });
        return out;
    }

    private async loadSGsAsTemplate(orgKey: string): Promise<OrgSecurityGroupTemplate[]> {
        const snap = await firstValueFrom(this.db.collection(`organizations/${orgKey}/securityGroups`).get());
        const out: OrgSecurityGroupTemplate[] = [];
        snap.forEach((d) => {
            const p = d.data() as Record<string, unknown>;
            if (p['isSoftDeleted'] === true) return;
            const key = String(p['key'] ?? d.id);
            if (!key) return;
            const sg: OrgSecurityGroupTemplate = {
                key,
                name: String(p['name'] ?? ''),
                features: Array.isArray(p['features'])
                    ? (p['features'] as unknown[]).filter((k): k is string => typeof k === 'string' && !!k)
                    : [],
                featureDetails: (p['featureDetails'] ?? {}) as Record<string, unknown>,
            };
            if (typeof p['description'] === 'string') sg.description = p['description'];
            if (Array.isArray(p['policies'])) {
                sg.policies = (p['policies'] as unknown[]).filter((k): k is string => typeof k === 'string' && !!k);
            }
            out.push(sg);
        });
        return out;
    }

    private async loadPoliciesAsTemplate(orgKey: string): Promise<OrgPolicyTemplate[]> {
        const snap = await firstValueFrom(this.db.collection(`organizations/${orgKey}/policies`).get());
        const out: OrgPolicyTemplate[] = [];
        snap.forEach((d) => {
            const p = d.data() as Record<string, unknown>;
            if (p['isSoftDeleted'] === true) return;
            if (p['isArchived'] === true) return;
            const key = String(p['key'] ?? d.id);
            if (!key) return;
            const policy: OrgPolicyTemplate = {
                key,
                name: String(p['name'] ?? ''),
                category: String(p['category'] ?? 'custom'),
                version: Number(p['version'] ?? 1),
                policyWriting: String(p['policyWriting'] ?? ''),
                status: String(p['status'] ?? 'draft'),
            };
            if (typeof p['description'] === 'string') policy.description = p['description'];
            if (typeof p['customCategory'] === 'string') policy.customCategory = p['customCategory'];
            if (typeof p['isPublished'] === 'boolean') policy.isPublished = p['isPublished'];
            if (p['effectiveDate']) policy.effectiveDate = p['effectiveDate'] as never;
            out.push(policy);
        });
        return out;
    }

    /**
     * Translate a live `OrgPart` to the wizard's `OrgDraftPart`. Drops
     * server-side metadata (createdOn, modifiedBy, isSoftDeleted, etc.); keeps
     * only fields the wizard knows how to render and round-trip.
     */
    private toDraftPart(p: Record<string, unknown>): OrgDraftPart {
        const part: OrgDraftPart = {
            partKey: String(p['partKey'] ?? ''),
            name: String(p['name'] ?? ''),
            partType: (p['partType'] as OrgDraftPart['partType']) ?? 'post',
            parentPartKey: String(p['parentPartKey'] ?? ''),
            id: Number(p['id'] ?? 0),
            depth: Number(p['depth'] ?? 0),
        };
        const colour = p['colour'] as { background?: string; color?: string; linkColor?: string } | undefined;
        if (colour && (colour.background || colour.color || colour.linkColor)) {
            (part as { colour?: { background: string; color: string; linkColor: string } }).colour = {
                background: colour.background ?? '',
                color: colour.color ?? '',
                linkColor: colour.linkColor ?? '',
            };
        }
        if (p['vfp']) part.vfp = String(p['vfp']);
        if (p['headerRole']) part.headerRole = p['headerRole'] as OrgDraftPart['headerRole'];
        if (p['isCeoPost'] === true) part.isCeoPost = true;
        return part;
    }

    /**
     * Return the orgKeys the current user is a member of. Reads
     * `users/{uid}.organizations` (the same map the header dropdown uses).
     */
    private async loadMyOrgKeys(): Promise<string[]> {
        const user = await firstValueFrom(
            this.auth.user.pipe(
                filter((u) => !!u),
                take(1)
            )
        );
        if (!user) return [];
        const userDoc = await firstValueFrom(
            this.db.doc<{ organizations?: Record<string, unknown> }>(`users/${user.uid}`).get()
        );
        const orgsMap = (userDoc.data()?.organizations ?? {}) as Record<string, unknown>;
        return Object.keys(orgsMap).filter((k) => k && typeof k === 'string');
    }

    /**
     * Fan out a Firestore query that uses `array-contains-any` or `in` (both
     * capped at 10 values by the SDK). Chunks `orgKeys` into 10-at-a-time
     * batches, runs the supplied query per chunk, and concatenates results.
     */
    private async runChunkedQuery(
        orgKeys: string[],
        queryForChunk: (chunk: string[]) => QueryFn<DocumentData>
    ): Promise<OrgTemplate[]> {
        const chunkSize = 10;
        const out: OrgTemplate[] = [];
        for (let i = 0; i < orgKeys.length; i += chunkSize) {
            const chunk = orgKeys.slice(i, i + chunkSize);
            const items = await firstValueFrom(
                this.db.collection<OrgTemplate>('orgTemplates', queryForChunk(chunk)).valueChanges({ idField: 'key' })
            );
            out.push(...(items ?? []));
        }
        return out;
    }

    /**
     * Cached lookup of an org's display name — used only for the org-private
     * source chip ("from <orgName>"). Best-effort: returns the orgKey itself
     * if the read is denied or the doc is missing.
     */
    private async resolveOrgName(orgKey: string, cache: Map<string, string>): Promise<string> {
        if (cache.has(orgKey)) return cache.get(orgKey) as string;
        try {
            const snap = await firstValueFrom(this.db.doc<{ name?: string }>(`organizations/${orgKey}`).get());
            const name = snap.data()?.name?.trim() || orgKey;
            cache.set(orgKey, name);
            return name;
        } catch {
            cache.set(orgKey, orgKey);
            return orgKey;
        }
    }

    private async summariseOrg(orgKey: string): Promise<CloneableOrgSummary | null> {
        try {
            const snap = await firstValueFrom(
                this.db
                    .doc<{
                        name?: string;
                        orgParts?: unknown[];
                        modifiedOn?: { toMillis?: () => number } | number;
                    }>(`organizations/${orgKey}`)
                    .get()
            );
            const data = snap.data();
            if (!data) return null;
            const partCount = Array.isArray(data.orgParts) ? data.orgParts.length : 0;
            const modifiedMs = this.toMillis(data.modifiedOn);
            return {
                orgKey,
                name: data.name?.trim() || `Unnamed (${orgKey.slice(0, 6)})`,
                partCount,
                modifiedMs,
            };
        } catch {
            // Rules deny / network error → just drop the org from the list.
            // The wizard never blocks on Clone — Excel + Scaffold + Blank still work.
            return null;
        }
    }

    private toMillis(v: unknown): number {
        if (!v) return 0;
        if (typeof v === 'number') return v;
        if (
            typeof v === 'object' &&
            v &&
            'toMillis' in v &&
            typeof (v as { toMillis?: () => number }).toMillis === 'function'
        ) {
            try {
                return (v as { toMillis: () => number }).toMillis();
            } catch {
                return 0;
            }
        }
        return 0;
    }
}
